import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { getWebSignInQRCodeStatus, } from 'actions';
import { AppView } from 'design-system/AppView';
import { CardView } from 'design-system/CardView';
import EmptyComponent from 'design-system/EmptyComponent';
import { Indicator } from 'design-system/Indicator';
import ModalWeb from 'design-system/ModalWeb';
import { QRCode } from 'design-system/QRCode';
import Text from 'design-system/Text';
import { IconEmma } from 'design-system/icons';
import { rem } from 'design-system/values';
import useOpenSupport from 'features/support/hooks/useOpenSupport';
import { muteAll, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import OnboardingMobileWeb from 'login/components/OnboardingMobileWeb.web';
import useSignIn from 'login/hooks/useSignIn';
import { useAppSelector, useAppStore } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import Spacer from 'design-system/Spacer';
import Links from 'utils/links';
const logo = require('../../../features/pay/img/bear@3x.png');
const OnBoardingScreen = ({ navigation, }) => {
    const { sidebarWidth } = useAppFrameDimensions(true);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: sidebarWidth ? 'Scan QR Code' : 'Welcome',
            headerTitle: EmptyComponent,
        });
    }, [sidebarWidth]);
    const [signIn, , , handleNextStep] = useSignIn({
        reset: true,
    });
    useEffect(() => {
        signIn({ requestQr: true });
    }, []);
    const clientId = useAppSelector((store) => store.signIn.currentState?.clientId);
    const qrStatus = useAppSelector((store) => store.signInWeb.webSignInConfirmation?.qrStatus);
    const [, , , fetch] = useFetchOld(muteAll);
    const store = useAppStore();
    useEffect(() => {
        const startTime = Date.now();
        const fiftyNineMinutes = 60 * 1000 * 59; // 59 minutes in milliseconds
        const timeout = setInterval(async () => {
            // Check if 59 minutes have passed - QR code becomes invalid after 1 hour
            if (Date.now() - startTime >= fiftyNineMinutes) {
                clearInterval(timeout);
                ModalWeb.showError('Your sign-in session has expired. Please scan the new QR code to continue.', () => signIn({ requestQr: true }), 'Session Expired', 'Try again');
                return;
            }
            const client = store.getState().signIn.currentState?.clientId;
            if (!client)
                return;
            const result = await fetch(getWebSignInQRCodeStatus({
                clientId: client,
            }));
            if (result?.qrStatus === 'AUTHORIZED') {
                clearInterval(timeout);
                handleNextStep(result.nextStep, { clientId: client });
            }
            else if (result?.qrStatus === 'DENIED' ||
                result?.qrStatus === 'LOGGED_IN') {
                clearInterval(timeout);
                signIn({ requestQr: true });
            }
        }, 2500);
        return () => {
            clearInterval(timeout);
        };
    }, [clientId]);
    const openContactSupport = useOpenSupport();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle, webStyles.contentContainer);
    const url = clientId ? `emma://web-sign-in?clientId=${clientId}` : '';
    const showLoading = !url || !clientId || qrStatus === 'OPENED';
    const openHelp = useCallback(() => {
        Links.openKnownLink('webSignInHelp');
    }, []);
    if (sidebarWidth === 0) {
        return <OnboardingMobileWeb />;
    }
    return (<AppView type="secondary" withFrame={false}>
      <ScrollView contentContainerStyle={contentContainerStyle}>
        <View>
          <IconEmma withText style={styles.logo} width={rem(92)} height={rem(37)}/>
          <Text Text-32 centered style={styles.title}>
            Sign in to Emma for Web
          </Text>
          <CardView style={styles.outerCard} noMargin>
            <CardView style={styles.qrCard} noMargin>
              {showLoading ? (<Indicator dark style={styles.qrLoadingIndicator}/>) : null}
              {url ? (<View style={showLoading ? styles.qrLoading : null}>
                  <QRCode value={url} ecl="quartile" logo={logo}/>
                </View>) : null}
            </CardView>
            {qrStatus === 'OPENED' ? (<View>
                <Text Text-24 style={styles.itemTitle}>
                  Waiting for your response...
                </Text>
                <Text TextThin-14 Secondary>
                  Approve this login by opening the Emma app and tapping
                  &quot;Approve and sign in&quot;.{'\n\n'}Need help?{' '}
                  <Text TextThin-14 Primary onPress={openContactSupport}>
                    Get in touch
                  </Text>
                </Text>
              </View>) : (<View>
                <Text Text-20 centered>
                  Scan the QR code to sign in
                </Text>
                <Spacer.H4 />
                <Text TextThin-16 centered Secondary>
                  Use your camera app or the QR scanner built in the Emma app -
                  just visit Account settings
                </Text>
                <View style={styles.helpContainer}>
                  <Text TextThin-16 Secondary>
                    Struggling to sign in?{' '}
                    <Text Text-16 Primary onPress={openHelp}>
                      Get help
                    </Text>
                  </Text>
                </View>
              </View>)}
          </CardView>
        </View>
      </ScrollView>
    </AppView>);
};
const webStyles = StyleSheet.create({
    contentContainer: {
        // @ts-expect-error
        display: 'grid',
        placeItems: 'center',
        // @ts-expect-error
        minHeight: '100vh',
    },
});
const styleSet = createStyleSheets((colors) => ({
    logo: {
        alignSelf: 'center',
        marginTop: rem(40),
    },
    title: {
        marginVertical: rem(40),
    },
    outerCard: {
        borderRadius: rem(24),
        padding: rem(24),
        marginHorizontal: rem(16),
        marginBottom: rem(40),
    },
    qrCard: {
        backgroundColor: colors.background.dark,
        padding: rem(26),
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: rem(40),
        borderWidth: rem(1),
        borderColor: colors.borders.divider,
    },
    qrLoading: {
        opacity: 0.1,
    },
    qrLoadingIndicator: {
        position: 'absolute',
    },
    itemContainer: {
        flexDirection: 'row',
        paddingVertical: rem(16),
        alignItems: 'center',
    },
    itemContainerLast: {
        paddingBottom: 0,
    },
    itemTitle: {
        marginBottom: rem(4),
    },
    itemTextContainer: {
        flexShrink: 1,
    },
    index: {
        width: rem(48),
        height: rem(48),
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: colors.buttons.brandGroup,
        borderRadius: rem(12),
        marginRight: rem(16),
    },
    helpContainer: {
        marginTop: rem(40),
        paddingTop: rem(16),
        alignItems: 'center',
        borderTopWidth: rem(1),
        borderTopColor: colors.borders.divider,
    },
}));
export default OnBoardingScreen;
